import { action, atom } from '@reatom/framework'
import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { Skeleton } from '@blade/ui-kit'
import {
  basicDepositAddressesStateAtom,
  loadBasicDepositAddressesAction,
} from '@root/entities/basic'
import { useTranslation } from '@root/shared/localization'
import { CopyButton, Dialog } from '@root/shared/ui-kit'

/**
 * Where a Basic balance comes from.
 *
 * Two addresses rather than six: one EVM key gives the same address on
 * Ethereum, BNB, Base, Arbitrum and Robinhood, so a deposit on any of them
 * lands in the same place. Both are generated once and never change, which is
 * why this screen can show them rather than issuing a new one per deposit.
 */

export const basicDepositDialogOpenAtom = atom(
  false,
  'basicDepositDialogOpenAtom',
)

export const openBasicDepositDialogAction = action((ctx) => {
  basicDepositDialogOpenAtom(ctx, true)
}, 'openBasicDepositDialogAction')

export const closeBasicDepositDialogAction = action((ctx) => {
  basicDepositDialogOpenAtom(ctx, false)
}, 'closeBasicDepositDialogAction')

export const BasicDepositDialog: React.FC = () => {
  const { t } = useTranslation()
  const [open] = useAtom(basicDepositDialogOpenAtom)
  const [state] = useAtom(basicDepositAddressesStateAtom)
  const close = useAction(closeBasicDepositDialogAction)
  const load = useAction(loadBasicDepositAddressesAction)

  useEffect(() => {
    // Asked for only when somebody opens this, because asking is what
    // creates the pair: a person who never deposits never needs one.
    if (open) {
      load()
    }
  }, [open, load])

  const addresses = pipe(
    state,
    AsyncStateN.getOrElseW(() => undefined),
  )

  return (
    <Dialog
      open={open}
      withClose
      describedBy={t('basic.deposit_title')}
      onOpenChange={(next) => {
        if (!next) {
          close()
        }
      }}
    >
      <Body>
        <Title>{t('basic.deposit_title')}</Title>
        <Hint>{t('basic.deposit_hint')}</Hint>

        {addresses ? (
          <>
            <AddressBlock
              label={t('basic.deposit_solana')}
              address={addresses.solana}
            />
            <AddressBlock
              label={t('basic.deposit_evm')}
              address={addresses.evm}
            />
          </>
        ) : (
          <>
            <Skeleton height={64} width="100%" />
            <Skeleton height={64} width="100%" />
          </>
        )}
      </Body>
    </Dialog>
  )
}

const AddressBlock: React.FC<{ label: string; address: string }> = ({
  label,
  address,
}) => (
  <Block>
    <BlockLabel>{label}</BlockLabel>
    <AddressRow>
      {/* Whole, not truncated: somebody comparing it against the one they
          pasted into an exchange has to be able to read every character. */}
      <Address>{address}</Address>
      <CopyButton value={address} />
    </AddressRow>
  </Block>
)

const Body = styled.div`
  display: grid;
  gap: 12px;
  padding: 20px;
  min-width: 380px;
`

const Title = styled.span`
  color: var(--text-12);
  font-size: 16px;
  font-weight: 700;
`

const Hint = styled.span`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 500;
`

const Block = styled.div`
  display: grid;
  gap: 6px;
  padding: 12px;

  background: var(--background-3);
  border-radius: 12px;
`

const BlockLabel = styled.span`
  color: var(--text-11);
  font-size: 11px;
  font-weight: 600;
`

const AddressRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
`

const Address = styled.span`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 500;
  word-break: break-all;
`
